import { Controller } from "@hotwired/stimulus"

/**
 * File Download Controller - Reusable file download/generation system
 *
 * This controller provides functionality for generating and downloading files
 * directly from the browser, particularly useful for CSV templates and exports.
 *
 * Usage examples:
 *
 * 1. CSV template download with inline data:
 *    <button data-controller="file-download"
 *            data-file-download-filename-value="template.csv"
 *            data-file-download-type-value="csv"
 *            data-file-download-headers-value='["name", "email", "phone"]'
 *            data-file-download-sample-value='["John Doe", "john@example.com", "555-1234"]'
 *            data-action="click->file-download#downloadTemplate">
 *      Download Template
 *    </button>
 *
 * 2. Download from URL:
 *    <button data-controller="file-download"
 *            data-file-download-filename-value="report.pdf"
 *            data-file-download-url-value="/reports/monthly.pdf"
 *            data-action="click->file-download#downloadTemplate">
 *      Download Report
 *    </button>
 *
 * 3. Quote bundle import template (specific use case):
 *    <button data-controller="file-download"
 *            data-file-download-filename-value="quote_bundle_import_template.csv"
 *            data-file-download-type-value="csv"
 *            data-file-download-headers-value='["street", "street_number", "zip_code", "cityname", "state_uf", "full_address", "service_type_id", "bandwidth", "comments"]'
 *            data-file-download-sample-value='["Rua das Flores", "123", "01310-100", "São Paulo", "SP", "Rua das Flores, 123, São Paulo - SP", "1", "100Mbps", "Premium client"]'
 *            data-action="click->file-download#downloadTemplate">
 *      Download Template
 *    </button>
 */
export default class extends Controller {
  static values = {
    filename: { type: String, default: "download.csv" },
    type: { type: String, default: "csv" },
    headers: { type: Array, default: [] },
    sample: { type: Array, default: [] },
    rows: { type: Array, default: [] },
    url: String,
    bom: { type: Boolean, default: true }
  }

  /**
   * Main action: Download template or file
   * Automatically chooses between URL download or client-side generation
   */
  downloadTemplate(event) {
    if (event) event.preventDefault()

    if (this.hasUrlValue && this.urlValue) {
      this.downloadFromUrl()
    } else {
      this.generateAndDownload()
    }
  }

  /**
   * Download file from a URL
   */
  downloadFromUrl() {
    const link = document.createElement('a')
    link.href = this.urlValue
    link.download = this.filenameValue
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  /**
   * Generate content and trigger download
   */
  generateAndDownload() {
    const content = this.generateContent()
    const mimeType = this.getMimeType()
    
    // Add BOM for UTF-8 CSV files to ensure proper encoding in Excel
    let blobContent = content
    if (this.typeValue === 'csv' && this.bomValue) {
      blobContent = '\ufeff' + content
    }
    
    const blob = new Blob([blobContent], { type: mimeType })
    
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = this.filenameValue
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Clean up the URL object
    URL.revokeObjectURL(url)
  }

  /**
   * Generate file content based on type
   */
  generateContent() {
    switch (this.typeValue) {
      case 'csv':
        return this.generateCSV()
      case 'json':
        return this.generateJSON()
      case 'txt':
        return this.generateText()
      default:
        return this.generateCSV()
    }
  }

  /**
   * Generate CSV content
   */
  generateCSV() {
    const lines = []
    
    // Add headers row
    if (this.headersValue.length > 0) {
      lines.push(this.escapeCsvRow(this.headersValue))
    }
    
    // Add sample row if provided
    if (this.sampleValue.length > 0) {
      lines.push(this.escapeCsvRow(this.sampleValue))
    }
    
    // Add additional rows if provided
    if (this.rowsValue.length > 0) {
      this.rowsValue.forEach(row => {
        if (Array.isArray(row)) {
          lines.push(this.escapeCsvRow(row))
        }
      })
    }
    
    return lines.join('\r\n')
  }

  /**
   * Generate JSON content
   */
  generateJSON() {
    const data = {
      headers: this.headersValue,
      sample: this.sampleValue,
      rows: this.rowsValue
    }
    return JSON.stringify(data, null, 2)
  }

  /**
   * Generate plain text content
   */
  generateText() {
    const lines = []
    
    if (this.headersValue.length > 0) {
      lines.push(this.headersValue.join('\t'))
    }
    
    if (this.sampleValue.length > 0) {
      lines.push(this.sampleValue.join('\t'))
    }
    
    if (this.rowsValue.length > 0) {
      this.rowsValue.forEach(row => {
        if (Array.isArray(row)) {
          lines.push(row.join('\t'))
        }
      })
    }
    
    return lines.join('\n')
  }

  /**
   * Escape and format a row for CSV
   * Handles commas, quotes, and newlines in cell values
   */
  escapeCsvRow(values) {
    return values.map(value => this.escapeCsvValue(value)).join(',')
  }

  /**
   * Escape a single CSV value
   */
  escapeCsvValue(value) {
    if (value === null || value === undefined) {
      return ''
    }
    
    const stringValue = String(value)
    
    // Check if escaping is needed
    const needsEscaping = stringValue.includes(',') ||
                          stringValue.includes('"') ||
                          stringValue.includes('\n') ||
                          stringValue.includes('\r')
    
    if (needsEscaping) {
      // Escape double quotes by doubling them
      const escaped = stringValue.replace(/"/g, '""')
      return `"${escaped}"`
    }
    
    return stringValue
  }

  /**
   * Get MIME type based on file type
   */
  getMimeType() {
    const types = {
      'csv': 'text/csv;charset=utf-8;',
      'json': 'application/json;charset=utf-8;',
      'txt': 'text/plain;charset=utf-8;',
      'xml': 'application/xml;charset=utf-8;'
    }
    return types[this.typeValue] || types['csv']
  }

  // Public methods for programmatic use

  /**
   * Set headers programmatically
   * @param {Array} headers - Array of header strings
   */
  setHeaders(headers) {
    this.headersValue = headers
  }

  /**
   * Set sample row programmatically
   * @param {Array} sample - Array of sample values
   */
  setSample(sample) {
    this.sampleValue = sample
  }

  /**
   * Add a row of data
   * @param {Array} row - Array of values
   */
  addRow(row) {
    this.rowsValue = [...this.rowsValue, row]
  }

  /**
   * Clear all rows
   */
  clearRows() {
    this.rowsValue = []
  }

  /**
   * Set filename
   * @param {string} filename
   */
  setFilename(filename) {
    this.filenameValue = filename
  }
}